import { logger } from 'utils/logging'
import { getImageMetadata } from 'utils/serialization/getImageMetadata'

/**
 * Common analytics data structure for photo studio events
 */
export interface PhotoStudioAnalyticsData {
  image_dimensions?: string
  image_format?: string
  sourceVisualFilename: string
  userEmail?: string
  organizationId?: string
  assignmentId?: string
  propertyType?: string
  ContentType?: string
  image_generation_sequence_number?: number
  photoStudioTrackId?: string
}

/**
 * Helper function to get common analytics data for photo studio events.
 * Returns image metadata, user info, assignmentId, propertyType, and sourceVisualFilename.
 *
 * @param file - The image file to extract metadata from
 * @param sourceVisualFilename - The source visual filename
 * @param userEmail - Optional user email
 * @param organizationId - Optional organization ID
 * @param assignmentId - Optional assignment ID
 * @param propertyType - Optional property type from assignment data
 * @param roomType - Optional room type (ContentType) for virtual staging events
 * @param imageGenerationSequenceNumber - Optional sequence number for generation events
 * @param trackId - Optional unique session ID to group all events from a single Photo Studio session
 * @returns Promise with common analytics data
 */
export const getCommonAnalyticsData = async (
  file: File,
  sourceVisualFilename: string,
  userEmail?: string,
  organizationId?: string,
  assignmentId?: string,
  propertyType?: string,
  roomType?: string | null,
  imageGenerationSequenceNumber?: number,
  trackId?: string
): Promise<PhotoStudioAnalyticsData> => {
  const commonData: PhotoStudioAnalyticsData = {
    sourceVisualFilename,
  }
  // Add trackId for session grouping in analytics
  if (trackId) {
    commonData.photoStudioTrackId = trackId
  }

  // Add user info if available
  if (userEmail) {
    commonData.userEmail = userEmail
  }

  if (organizationId) {
    commonData.organizationId = organizationId
  }

  // Add assignmentId if available
  if (assignmentId) {
    commonData.assignmentId = assignmentId
  }

  // Add propertyType if available
  if (propertyType) {
    commonData.propertyType = propertyType
  }

  // Add image_generation_sequence_number if provided
  if (imageGenerationSequenceNumber !== undefined) {
    commonData.image_generation_sequence_number = imageGenerationSequenceNumber
  }

  // Try to get image metadata
  try {
    const metadata = await getImageMetadata(file)
    commonData.image_dimensions = `${metadata.width}x${metadata.height}`
    commonData.image_format = file.type
  } catch (error) {
    logger.warn('Image metadata extraction failed', error, {
      context: {
        component: 'PhotoStudio',
        action: 'getImageMetadata',
      }
    })
  }

  return commonData
}
